import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

export default function UpdatePassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        setCheckingSession(false);
        if (!session) {
          setErrorMsg('Este link de recuperação é inválido ou expirou. Solicite um novo link.');
        }
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (password.length < 6) {
      setErrorMsg('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg('As senhas não coincidem.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setErrorMsg(error.message);
      } else {
        setUpdated(true);
      }
    } catch (err: unknown) {
      setErrorMsg('Ocorreu um erro ao atualizar a senha. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (updated) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[80vh] bg-gradient-to-tr from-primary-50 to-background py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-primary-100 p-8 sm:p-10 text-center">
          <CheckCircle2 size={64} className="mx-auto mb-6 text-primary-500" />
          <h2 className="text-3xl font-extrabold text-primary-900 mb-4">Senha atualizada</h2>
          <p className="text-neutral-600 leading-relaxed mb-8">Sua senha foi alterada com sucesso.</p>
          <button
            type="button"
            onClick={() => navigate('/cowork/login')}
            className="inline-flex items-center gap-2 bg-primary-700 hover:bg-primary-900 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Ir para o login
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center min-h-[80vh] bg-gradient-to-tr from-primary-50 to-background py-16 px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-primary-100 p-8 sm:p-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-primary-900 tracking-tight">Criar nova senha</h2>
          <p className="text-neutral-500 mt-2 text-sm">Escolha uma senha segura para sua conta.</p>
        </div>

        {errorMsg && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm mb-6 border border-red-200">
            {errorMsg}
          </div>
        )}

        {!checkingSession && !errorMsg && (
          <form onSubmit={handleUpdatePassword} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">Nova senha</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                  <Lock size={18} />
                </span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo de 6 caracteres"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">Confirmar nova senha</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                  <Lock size={18} />
                </span>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Digite a senha novamente"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 bg-primary-700 hover:bg-primary-900 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : null}
              {loading ? 'Atualizando...' : 'Atualizar senha'}
            </button>
          </form>
        )}

        {errorMsg && (
          <div className="mt-6 text-center">
            <Link to="/cowork/forgot-password" className="text-sm text-primary-700 font-bold hover:text-primary-900 transition-colors">
              Solicitar um novo link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}